// /bills — Bills list (admin)
//
// Read-mostly page for finding a specific bill or spotting anomalies. The
// shared filter bar (T27) keeps every filter in the URL (the back button
// works, refresh keeps them, links can be shared), saves favourites on the
// server, and the summary is the server's count and total over EVERY
// matching bill — the same rows the export writes.

import { useState } from 'react';
import { formatBillNumber } from '../../features/bills/bill-number';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Skeleton,
  Alert,
  EmptyState,
  Select,
  useToast,
  FilterBar,
  SavedFiltersMenu,
  ListSummaryBar,
} from '@bbpos/ui';
import type { FilterBarFacet, FilterBarSelect } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { GLASS, cn } from '@/constants/design-tokens';
import { useBillsList } from '@/features/bills';
import type { BillListFilters, BillListItem, BillStatus, PaymentMethod } from '@/features/bills';
import { useListFilterBar, useMigrateLegacyBillViews } from '@/features/saved-filters';
import { BillStatusPill } from './status-meta';
import { downloadBillsCsv } from './export-bills-csv';
import { useAuthStore } from '@/stores/use-auth-store';
import { useStoreTimezone } from '@/lib/store-clock';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** On the store's clock (T18), not this browser's. */
function formatDateTime(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Filters (T27) ────────────────────────────────────────────────────────────

/** The Bills list's query parameters — the API's own names. */
export const BILL_FILTER_KEYS = [
  'customer_name',
  'customer_phone',
  'bill_number',
  'search',
  'status',
  'payment_method',
  'location_id',
  'start_date',
  'end_date',
  'customer_id',
];

const BILL_FACETS: FilterBarFacet[] = [
  { key: 'customer_name', label: 'Customer' },
  { key: 'customer_phone', label: 'Mobile', kind: 'phone' },
  { key: 'bill_number', label: 'Bill no.', kind: 'number' },
  // Bill number, customer or mobile in one — what the old single search box did.
  { key: 'search', label: 'Any of them' },
];

const STATUS_OPTIONS: { value: BillStatus; label: string }[] = [
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PARKED', label: 'Parked' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'VOID', label: 'Void' },
  { value: 'PENDING_DISCOUNT_APPROVAL', label: 'Awaiting discount approval' },
];

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CARD', label: 'Card' },
  { value: 'WALLET', label: 'Wallet' },
  { value: 'SPLIT', label: 'Split' },
  { value: 'CREDIT', label: 'Credit (unpaid)' },
];

const BILL_DATE = { fromKey: 'start_date', toKey: 'end_date' };

const PAGE_SIZES = [20, 50, 100];

// ─── Payment method chip ──────────────────────────────────────────────────────

const PAYMENT_CHIP: Record<string, string> = {
  CASH:   'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-200',
  UPI:    'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200',
  CARD:   'bg-info-100 text-info-700 dark:bg-info-900/40 dark:text-info-200',
  WALLET: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-200',
  SPLIT:  'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-200',
  CREDIT: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200',
};

function PaymentChip({ method }: { method?: string }) {
  if (!method) return <span className="text-neutral-400">—</span>;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
        PAYMENT_CHIP[method] ?? 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700/60 dark:text-neutral-200',
      )}
    >
      {method}
    </span>
  );
}

// ─── Pagination footer ────────────────────────────────────────────────────────

function Pagination({
  page,
  totalPages,
  limit,
  total,
  onPage,
  onLimit,
}: {
  page: number;
  totalPages: number;
  limit: number;
  total: number;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
}) {
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(total, page * limit);

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-t border-neutral-100 dark:border-neutral-700/60">
      <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
        {total === 0
          ? 'No bills'
          : `Showing ${start.toLocaleString('en-IN')}–${end.toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')}`}
      </p>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">Per page</span>
          <Select
            options={[
              { value: '20',  label: '20' },
              { value: '50',  label: '50' },
              { value: '100', label: '100' },
            ]}
            value={String(limit)}
            onChange={(v) => {
              const next = Array.isArray(v) ? v[0] : v;
              onLimit(parseInt(next, 10));
            }}
          />
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
          >
            ← Prev
          </Button>
          <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums mx-2">
            Page {page} of {Math.max(1, totalPages)}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPage(page + 1)}
          >
            Next →
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function BillListPage() {
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const [searchParams] = useSearchParams();
  const profile = useAuthStore((s) => s.profile);
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  useMigrateLegacyBillViews();
  const bar = useListFilterBar({ page: 'bills', keys: BILL_FILTER_KEYS, date: BILL_DATE, locationKey: 'location_id' });
  const { values } = bar;

  const pageParam = parseInt(searchParams.get('page') ?? '1', 10);
  const limitParam = parseInt(searchParams.get('limit') ?? '20', 10);
  const filters: BillListFilters = {
    page: Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1,
    limit: PAGE_SIZES.includes(limitParam) ? limitParam : 20,
    status: values.status as BillStatus | undefined,
    locationId: values.location_id,
    startDate: values.start_date,
    endDate: values.end_date,
    paymentMethod: values.payment_method as PaymentMethod | undefined,
    search: values.search,
    customerName: values.customer_name,
    customerPhone: values.customer_phone,
    billNumber: values.bill_number,
    customerId: values.customer_id,
  };
  const hasActiveFilters = Object.keys(values).length > 0;

  const { data, isLoading, isError, error, isFetching } = useBillsList(filters);
  const bills = data?.data ?? [];
  const meta = data?.meta;

  const selects: FilterBarSelect[] = [
    { key: 'status', label: 'Status', options: STATUS_OPTIONS, allLabel: 'All statuses' },
    { key: 'payment_method', label: 'Payment', options: PAYMENT_OPTIONS, allLabel: 'All payments' },
  ];
  const activeLocations = (profile?.locations ?? []).filter((l) => l.is_active);
  if (activeLocations.length > 1) {
    selects.push({
      key: 'location_id',
      label: 'Location',
      options: activeLocations.map((l) => ({ value: l.id, label: l.name })),
      allLabel: 'All locations',
    });
  }

  // T20: the API writes every bill the filters match, not just this page.
  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadBillsCsv(filters);
    } catch (err) {
      toast({ title: (err as Error).message || 'Export failed', variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageContainer
      title=""
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Bills' }]}
    >
      <div className="space-y-6 animate-fade-in">
        {/* Gradient header */}
        <header className="bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
          <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-12 h-44 w-44 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">Bills</h1>
              <p className="mt-1.5 text-sm text-white/85">
                Find any bill, audit voids and returns, or export a slice for accounting.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                onClick={handleExport}
                disabled={!meta?.total || isExporting}
              >
                {isExporting ? 'Exporting…' : 'Export CSV'}
              </Button>
            </div>
          </div>
        </header>

        <ListSummaryBar
          loading={isLoading}
          items={[
            { label: 'Bills matching', value: (meta?.total ?? 0).toLocaleString('en-IN'), tone: 'brand' },
            {
              label: 'Total of these bills',
              value: formatPaise(meta?.summary?.grand_total ?? 0, currency),
              tone: 'success',
            },
          ]}
        />

        <FilterBar
          facets={BILL_FACETS}
          selects={selects}
          date={BILL_DATE}
          values={values}
          onChange={bar.onChange}
          onReset={bar.onReset}
          timeZone={bar.timeZone}
          placeholder="Type a customer, mobile or bill number…"
          favorites={<SavedFiltersMenu {...bar.favorites} />}
        />

        {/* Opened from a customer's page: their bills only, until removed. */}
        {values.customer_id && (
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-200 px-3 py-1 text-xs font-semibold">
              One customer&apos;s bills
              <button
                type="button"
                aria-label="Clear customer filter"
                className="hover:text-pink-900 dark:hover:text-pink-100"
                onClick={() => bar.onChange({ customer_id: undefined })}
              >
                ×
              </button>
            </span>
          </div>
        )}

        {/* Results */}
        <section className={cn(GLASS, 'overflow-hidden')}>
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rect" height={48} />
              ))}
            </div>
          ) : isError ? (
            <div className="p-5">
              <Alert
                variant="danger"
                title="Failed to load bills"
                description={error instanceof Error ? error.message : 'Try again or clear filters.'}
              />
            </div>
          ) : bills.length === 0 ? (
            <div className="p-8">
              <EmptyState
                variant="section"
                title="No bills match these filters"
                description={
                  hasActiveFilters
                    ? 'Try widening the date range or clearing a filter.'
                    : 'Once your team starts billing, every completed sale will land here.'
                }
                action={hasActiveFilters ? { label: 'Clear filters', onClick: bar.onReset } : undefined}
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 dark:bg-neutral-800/40 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    <tr>
                      <th className="px-5 py-2 text-left">Bill #</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Customer</th>
                      <th className="px-3 py-2 text-left">Cashier</th>
                      <th className="px-3 py-2 text-right">Items</th>
                      <th className="px-3 py-2 text-left">Payment</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-5 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((b) => (
                      <BillRow key={b.id} bill={b} currency={currency} onClick={() => navigate(`/bills/${b.id}`)} />
                    ))}
                  </tbody>
                </table>
              </div>
              {meta && (
                <Pagination
                  page={meta.page}
                  totalPages={meta.totalPages}
                  limit={meta.limit}
                  total={meta.total}
                  onPage={(p) => bar.onChange({ page: String(p) })}
                  onLimit={(l) => bar.onChange({ limit: String(l), page: undefined })}
                />
              )}
            </>
          )}

          {/* Subtle "refreshing" line — appears when paginating while previous
              data is still visible (placeholderData kept). Better than a full
              skeleton flash. */}
          {isFetching && !isLoading && bills.length > 0 && (
            <div className="px-5 pb-2 text-[11px] text-neutral-400 italic">Refreshing…</div>
          )}
        </section>
      </div>
    </PageContainer>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function BillRow({
  bill,
  currency,
  onClick,
}: {
  bill: BillListItem;
  currency: string;
  onClick: () => void;
}) {
  const timeZone = useStoreTimezone(bill.location_id);
  return (
    <tr
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick();
      }}
      className="border-t border-neutral-100 dark:border-neutral-700/60 hover:bg-brand-50/40 dark:hover:bg-neutral-800/60 cursor-pointer focus:outline-none focus:bg-brand-50 dark:focus:bg-neutral-700"
    >
      <td className="px-5 py-2.5">
        <span className="font-mono tabular-nums text-sm font-semibold text-brand-700 dark:text-brand-300">
          {formatBillNumber(bill)}
        </span>
        {bill.location_name && (
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
            {bill.location_name}
          </p>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-neutral-600 dark:text-neutral-300 tabular-nums whitespace-nowrap" title={bill.created_at}>
        {formatDateTime(bill.created_at, timeZone)}
      </td>
      <td className="px-3 py-2.5">
        {bill.customer_name ? (
          <>
            <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate max-w-[20ch]">
              {bill.customer_name}
            </p>
            {bill.customer_phone && (
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 tabular-nums">
                {bill.customer_phone}
              </p>
            )}
          </>
        ) : (
          <span className="inline-flex items-center rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 px-2 py-0.5 text-[11px] font-semibold">
            Walk-in
          </span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {bill.created_by_name ? (
          <p className="text-sm text-neutral-800 dark:text-neutral-100 truncate max-w-[18ch]">
            {bill.created_by_name}
          </p>
        ) : (
          <span className="text-[11px] text-neutral-400">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-neutral-700 dark:text-neutral-200">
        {bill.item_count}
      </td>
      <td className="px-3 py-2.5">
        <PaymentChip method={bill.payment_method} />
      </td>
      <td className="px-3 py-2.5">
        <BillStatusPill status={bill.status} />
      </td>
      <td className="px-5 py-2.5 text-right font-mono tabular-nums font-semibold text-neutral-900 dark:text-white">
        {formatPaise(bill.grand_total + (bill.rounding_adjustment ?? 0), currency as 'INR')}
      </td>
    </tr>
  );
}
